/**
 * useAuth — auth lifecycle + readers.
 *
 *   useAuthInit()  → mount once; subscribes to Supabase auth state changes.
 *   useSignOut()   → sign out + reset store.
 *   useRole()      → current role from JWT app_metadata.
 *   useOrgId()     → current org_id from JWT app_metadata.
 *
 * Sign-in actions live in:
 *   - useSignInWithPin (PR B) — email + PIN bootstrap path.
 *   - useSignInWithBiometric (PR C, pending) — passwordless WebAuthn.
 *
 * The legacy `useSignIn` (signInWithPassword) was removed in PR B — the
 * platform never issues passwords; admin-provisioned PIN is the only
 * bootstrap credential.
 */

import { useEffect }                            from 'react'
import { supabase }                             from '@/lib/supabase'
import { useAuthStore, selectRole, selectOrgId, selectUserId } from '@/store/authStore'
import type { Role } from '@/types/roles'

export function useAuthInit(): void {
  const setSession = useAuthStore((s) => s.setSession)
  const setLoading = useAuthStore((s) => s.setLoading)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    supabase.auth.getSession().then(({ data, error }) => {
      if (cancelled) return
      if (error) {
        setSession(null)
      } else {
        setSession(data.session)
      }
    })

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return
      setSession(session)
    })

    return () => {
      cancelled = true
      data.subscription.unsubscribe()
    }
  }, [setSession, setLoading])
}

export function useSignOut(): () => Promise<void> {
  const reset = useAuthStore((s) => s.reset)
  return async (): Promise<void> => {
    await supabase.auth.signOut()
    reset()
  }
}

export function useRole():   Role   | null { return useAuthStore(selectRole)   }
export function useOrgId():  string | null { return useAuthStore(selectOrgId)  }
export function useUserId(): string | null { return useAuthStore(selectUserId) }
